import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate, useLocation, matchPath } from 'react-router-dom';
import { Box, CssBaseline, useTheme, useMediaQuery, List, Drawer, ListItem, ListItemText, ListItemButton, Avatar, Typography, ListItemIcon, Collapse, Menu, MenuItem, Chip } from '@mui/material';
import { Sidebar } from '@ui/components/Sidebar';
import { BottomNav } from '@ui/components/BottomNav';
import { KeyboardArrowRight as ChevronRightIcon } from '@mui/icons-material';
import { useSidebar, HelpTopicProvider } from '@ui/contexts';
import useLayoutStyles from './useLayoutStyles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import LocalStorageService from '@application/common/local-storage-service';
import { logout, startRolePreview, stopRolePreview } from '@application/features/auth/authSlice';
import { ROLE_PREVIEW_OPTIONS } from '@application/constants/role-preview-permissions';
import RolePreviewBanner from '@ui/components/RolePreviewBanner';
import { useAppDispatch } from '@application/store/useStore';
import { Coin, Lock, MagicStar, Profile2User, Task, UserCirlceAdd, NotificationBing, Grid2 } from 'iconsax-react';
import i18n from '@application/utilities/i18n';
import LanguageMenu from './LanguageMenu';
import { Icon } from '@iconify/react';
import { setSelectedCompanyId } from '@application/features/company/companySlice';
import CompanyMenu from './companyMenu';
import StyledSearchInput from '@ui/components/StyledSearchInput';
import { SubMenuIcon } from '@ui/assets/custom-icons/submenu-icon';
import HelpCenter from '@ui/components/common/HelpCenter';
import { refreshTokenAsync } from '@application/features/auth/authThunk';
// Bottom navigation items for mobile/tablet
// User data
const user = {
    name: 'Claudia Van Der Kolk',
    group: 'VDK Groep',
    onUserClick: () => console.log('User profile clicked')
};
// Inner layout component that uses the sidebar context
const AppLayoutContent = () => {
    const styles = useLayoutStyles();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const theme = useTheme();
    const selectedUser = useSelector((state) => state.auth.currentUser);
    const isPreviewMode = useSelector((state) => state.auth.isPreviewMode);
    const previewRoleLabel = useSelector((state) => state.auth.previewRoleLabel);
    const originalCurrentUser = useSelector((state) => state.auth.originalCurrentUser);
    const realUserRoles = isPreviewMode ? originalCurrentUser?.role : selectedUser?.role;
    const isHappinessManager = realUserRoles?.includes('HappinessManager') ?? false;
    const isCollaborator = Array.isArray(selectedUser?.role) && (selectedUser?.role.includes('Collaborator') || selectedUser?.role.includes('TransportManager'));
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const [companyAnchor, setCompanyAnchor] = useState(null);
    const { isOpen, activeItem, navigationSections, toggleSidebar, setActiveItem, onSearch } = useSidebar();
    const profilePicture = selectedUser?.ProfilePicturePath;
    const handleCompanyClose = () => setCompanyAnchor(null);
    const companies = useSelector((state) => state.company.companies);
    const selectedCompanyId = useSelector((state) => state.company.selectedCompanyId);
    const handleSelectCompany = (companyId) => {
        // Toggle company selection (multi-select)
        const updatedSelection = selectedCompanyId.includes(companyId)
            ? selectedCompanyId.filter((id) => id !== companyId)
            : [...selectedCompanyId, companyId];
        LocalStorageService.setSelectedCompanyIdForTab(updatedSelection);
        dispatch(setSelectedCompanyId(updatedSelection));
        dispatch(refreshTokenAsync());
    };
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [expandedDrawerItems, setExpandedDrawerItems] = useState(new Set());
    const [notificationDrawerOpen, setNotificationDrawerOpen] = useState(false);
    const [previewMenuAnchor, setPreviewMenuAnchor] = useState(null);
    const getInitials = (name) => {
        if (!name)
            return '';
        const parts = name.trim().split(' ').filter(Boolean);
        const { isOpen, activeItem, navigationSections, setActiveItem, onSearch } = useSidebar();
        if (parts.length === 0)
            return '';
        if (parts.length === 1)
            return parts[0][0].toUpperCase();
        const firstInitial = parts[0][0].toUpperCase();
        const lastInitial = parts[parts.length - 1][0].toUpperCase();
        return `${firstInitial}${lastInitial}`;
    };
    const userInitials = getInitials(selectedUser?.name);
    const [userMenuAnchor, setUserMenuAnchor] = useState(null);
    const createBottomNavItems = (navigate) => {
        const items = [
            {
                id: 'home',
                label: t('common.dashboard'),
                icon: _jsx(Grid2, { variant: 'Outline' }),
                onClick: () => navigate('/')
            },
            {
                id: 'events',
                label: t('common.events'),
                icon: _jsx(MagicStar, { variant: 'Outline' }),
                onClick: () => navigate('/events')
            },
            {
                id: 'tasks',
                label: t('common.tasks'),
                icon: _jsx(Task, { variant: 'Outline' }),
                onClick: () => navigate('/tasks')
            },
            {
                id: 'profile',
                label: t('common.profile'),
                icon: (_jsx(Avatar, { variant: 'square', src: profilePicture || undefined, sx: {
                        width: 32,
                        height: 32,
                        fontSize: 14,
                        alignItems: 'center',
                        backgroundColor: theme.palette.grey[300],
                        color: theme.palette.grey[500],
                        borderRadius: '12px !important'
                    }, alt: 'User', children: !profilePicture && userInitials })),
                onClick: () => setDrawerOpen(true)
            }
        ];
        if (isCollaborator) {
            return items.filter((i) => i.id === 'home' || i.id === 'events' || i.id === 'tasks' || i.id === 'profile');
        }
        return items;
    };
    // Create bottom nav items with navigation
    const bottomNavItems = createBottomNavItems(navigate);
    // Languages
    const languages = [
        { textKey: 'English', code: 'en', flag: '/assets/images/flags/us-flag.svg' },
        { textKey: 'Dutch', code: 'nl', flag: '/assets/images/flags/nl-flag.svg' },
        { textKey: 'German', code: 'de', flag: '/assets/images/flags/de-flag.svg' }
    ];
    const initialLang = languages.find((lang) => lang.code === i18n.language) || languages[0];
    const [selectedLang, setSelectedLang] = useState(initialLang);
    // Sync selected language with i18n language changes (e.g., when user's primary language is set on login)
    useEffect(() => {
        const currentLang = languages.find((lang) => lang.code === i18n.language) || languages[0];
        setSelectedLang((prevLang) => {
            if (prevLang.code !== currentLang.code) {
                return currentLang;
            }
            return prevLang;
        });
    }, [i18n.language]);
    // Determine active bottom nav item based on current route
    const getActiveBottomNavItem = () => {
        const path = location.pathname;
        if (path === '/')
            return 'home';
        if (path.startsWith('/events'))
            return 'events';
        if (path.startsWith('/tasks'))
            return 'tasks';
        if (path.startsWith('/search'))
            return 'search';
        if (path.startsWith('/profile'))
            return 'profile';
        return 'home';
    };
    const [languageAnchor, setLanguageAnchor] = useState(null);
    const handleLanguageClose = () => {
        setLanguageAnchor(null);
    };
    const dispatch = useAppDispatch();
    const handleLogout = () => {
        // Soft application logout: clear app tokens and mark MSAL logout to prevent auto-login
        dispatch(logout());
        LocalStorageService.removeAccessToken();
        LocalStorageService.removeMSAccessToken();
        // Mark that user intentionally logged out so redirect handling ignores any MSAL state
        try {
            sessionStorage.setItem('msal_logout', 'true');
            // Also clear any pending login initiation flag
            sessionStorage.removeItem('msal_login_initiated');
        }
        catch (e) {
            // ignore storage errors
        }
        // Navigate to login page (soft logout - keeps Microsoft session)
        navigate('/login');
    };
    const handleSelectLanguage = (lang) => {
        console.log('Selected language:', lang);
        setLanguageAnchor(null);
    };
    const drawerSections = (() => {
        const base = [
            {
                highlight: true,
                items: [
                    {
                        id: 'logout',
                        label: t('users.logout'),
                        color: 'error',
                        onClick: handleLogout,
                        icon: (_jsx(Icon, { icon: 'solar:logout-2-bold-duotone', width: '20', height: '20', color: '#FF5630' }))
                    }
                ]
            },
            {
                highlight: true,
                items: [
                    {
                        id: 'users',
                        label: t('common.users'),
                        color: 'primary',
                        href: '/users',
                        onClick: () => {
                            setDrawerOpen(false);
                            navigate('users');
                        },
                        icon: _jsx(Profile2User, { variant: 'Outline' })
                    },
                    {
                        id: 'collaborators',
                        label: t('common.collaborators'),
                        color: 'primary',
                        href: '/collaborators',
                        onClick: () => {
                            setDrawerOpen(false);
                            navigate('collaborators');
                        },
                        icon: _jsx(UserCirlceAdd, { variant: 'Outline' })
                    },
                    {
                        id: 'roles',
                        label: t('common.rolesPermissions'),
                        color: 'primary',
                        href: '/roles-permissions',
                        onClick: () => {
                            setDrawerOpen(false);
                            navigate('roles-permissions');
                        },
                        icon: _jsx(Lock, { variant: 'Outline' })
                    },
                    {
                        id: 'notifications',
                        label: t('notifications.title'),
                        color: 'primary',
                        href: '/notifications',
                        onClick: () => {
                            setDrawerOpen(false);
                            navigate('/notifications');
                        },
                        icon: _jsx(NotificationBing, { variant: 'Outline' })
                    },
                    {
                        id: 'people',
                        label: t('common.people'),
                        color: 'primary',
                        hasSubmenu: true,
                        icon: _jsx(Profile2User, { variant: 'Outline' }),
                        submenuItems: [
                            {
                                id: 'vdk-people',
                                label: t('relations.vdkPeople'),
                                href: '/people',
                                onClick: () => {
                                    setDrawerOpen(false);
                                    navigate('/people');
                                }
                            },
                            {
                                id: 'relations-management',
                                label: t('relations.relationsManagement'),
                                href: '/relations',
                                onClick: () => {
                                    setDrawerOpen(false);
                                    navigate('/relations');
                                }
                            }
                        ]
                    },
                    {
                        id: 'sponsorships',
                        label: t('common.sponsorships'),
                        color: 'primary',
                        href: '/sponsorships',
                        onClick: () => {
                            setDrawerOpen(false);
                            navigate('sponsorships');
                        },
                        icon: _jsx(Coin, { variant: 'Outline' })
                    }
                ]
            }
        ];
        if (isCollaborator) {
            return [
                {
                    highlight: true,
                    items: base[0].items // only logout
                }
            ];
        }
        return base;
    })();
    // Update navigation sections with active state
    const navigationSectionsWithActive = navigationSections.map((section) => ({
        ...section,
        items: section.items.map((item) => ({
            ...item,
            isActive: activeItem === item.id
        }))
    }));
    const handleSearch = (value) => {
        console.log('Search query:', value);
        onSearch(value);
    };
    const handleNavigationItemClick = (item) => {
        setActiveItem(item.id);
        if (item.onClick) {
            item.onClick();
        }
    };
    const handleBottomNavClick = (item) => {
        setActiveItem(item.id);
        if (item.id === 'profile') {
            // Open bottom drawer instead of navigating
            setDrawerOpen(true);
            return;
        }
        if (item.onClick) {
            item.onClick();
        }
    };
    // A helper to check active state
    const isActive = (href) => {
        if (!href)
            return false;
        return location.pathname === href;
    };
    // Handler for toggling drawer submenu items
    const handleDrawerSubmenuToggle = (itemId) => {
        setExpandedDrawerItems((prev) => {
            const newExpanded = new Set(prev);
            if (newExpanded.has(itemId)) {
                newExpanded.delete(itemId);
            }
            else {
                newExpanded.add(itemId);
            }
            return newExpanded;
        });
    };
    // NOTE: Company selection is initialized centrally (Sidebar) to avoid conflicting defaults.
    // Auto-expand drawer items if their submenu items are active
    useEffect(() => {
        // Check if we're on a people or relations page to auto-expand the people menu
        const peoplePaths = ['/people', '/relations'];
        const isOnPeopleRelatedPage = peoplePaths.some((path) => location.pathname === path || matchPath({ path, end: false }, location.pathname));
        if (isOnPeopleRelatedPage) {
            setExpandedDrawerItems((prev) => new Set(prev).add('people'));
        }
    }, [location.pathname]);
    const selectedCompany = useMemo(() => {
        if (selectedCompanyId.length === 0)
            return '';
        if (selectedCompanyId.length === 1) {
            return companies.find((c) => c.id === selectedCompanyId[0])?.name || '';
        }
        return `${selectedCompanyId.length} ${t('common.companiesSelected') || 'companies selected'}`;
    }, [selectedCompanyId, companies, t]);
    return (_jsx(HelpTopicProvider, { children: _jsxs(Box, { sx: { display: 'flex', width: '100%', overflowX: 'hidden' }, children: [_jsx(CssBaseline, {}), _jsx(Sidebar, { logoText: 'Happiness', user: user, searchPlaceholder: t('placeholders.searchAnything') + '...', onSearch: handleSearch, navigationSections: navigationSectionsWithActive, onNavigationItemClick: handleNavigationItemClick, open: isOpen, width: 284, collapsedWidth: 88 }), isPreviewMode && _jsx(RolePreviewBanner, {}), _jsx(Box, { component: 'main', sx: {
                        ...styles.layoutMainFull,
                        overflowX: 'auto',
                    }, children: _jsx(Outlet, {}) }), !location.pathname.includes('/create-event') && (_jsx(BottomNav, { items: bottomNavItems, activeItem: getActiveBottomNavItem(), onItemClick: handleBottomNavClick })), _jsx(Drawer, { anchor: 'bottom', open: drawerOpen, onClose: () => setDrawerOpen(false), PaperProps: {
                        sx: {
                            height: 'auto',
                            maxHeight: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            borderTopRightRadius: '16px',
                            borderTopLeftRadius: '16px'
                        }
                    }, children: _jsx(Box, { sx: {
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            flex: 1,
                            px: 2,
                            py: 2
                        }, children: _jsxs(List, { sx: { p: 0, display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsxs(Box, { sx: {
                                        p: 2,
                                        borderRadius: 2,
                                        bgcolor: theme.palette.grey[50],
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1
                                    }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 2 }, children: [_jsx(Avatar, { variant: 'square', src: profilePicture || undefined, sx: {
                                                        width: 40,
                                                        height: 40,
                                                        backgroundColor: theme.palette.grey[400],
                                                        color: theme.palette.grey[600],
                                                        borderRadius: '12px !important'
                                                    }, alt: 'User', children: !profilePicture && userInitials }), _jsx(ListItemText, { primary: selectedUser?.name, primaryTypographyProps: { fontWeight: 600 } })] }), _jsx(StyledSearchInput, { onClick: () => {
                                                localStorage.setItem('globalSearchAutoFocus', 'true');
                                                navigate('/global-search');
                                                setDrawerOpen(false);
                                            }, readOnly: true, fullWidth: true, bordered: false }), _jsx(ListItem, { disablePadding: true, children: _jsxs(ListItemButton, { onClick: (e) => setLanguageAnchor(e.currentTarget), sx: { px: 0 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }, children: [_jsx("img", { src: selectedLang.flag, alt: selectedLang.code, style: { width: 20, height: 20 } }), _jsx(Typography, { children: selectedLang.textKey })] }), _jsx(ChevronRightIcon, { sx: { color: theme.palette.grey[500] } })] }) }), _jsx(ListItem, { disablePadding: true, children: _jsxs(ListItemButton, { onClick: (e) => setCompanyAnchor(e.currentTarget), sx: { px: 0 }, children: [_jsx(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }, children: _jsx(Typography, { children: selectedCompany }) }), _jsx(ChevronRightIcon, { sx: { color: theme.palette.grey[500] } })] }) }), isHappinessManager && (_jsx(ListItem, { disablePadding: true, children: isPreviewMode ? (_jsx(Box, { sx: { width: '100%', py: 0.5, px: 0 }, children: _jsx(Chip, { label: t('rolePreview.previewingChip', { role: previewRoleLabel ? t(previewRoleLabel) : '' }), size: 'small', icon: _jsx(Icon, { icon: 'solar:eye-bold', width: 14 }), onDelete: () => { dispatch(stopRolePreview()); navigate('/'); }, color: 'warning', sx: { width: '100%' } }) })) : (_jsxs(ListItemButton, { onClick: (e) => setPreviewMenuAnchor(e.currentTarget), sx: { px: 0 }, children: [_jsx(ListItemIcon, { sx: { minWidth: 32 }, children: _jsx(Icon, { icon: 'solar:eye-bold', width: 20 }) }), _jsx(ListItemText, { primary: t('rolePreview.previewAsRole') }), _jsx(ChevronRightIcon, { sx: { color: theme.palette.grey[500] } })] })) })), _jsx(Menu, { anchorEl: previewMenuAnchor, open: Boolean(previewMenuAnchor), onClose: () => setPreviewMenuAnchor(null), anchorOrigin: { vertical: 'top', horizontal: 'right' }, transformOrigin: { vertical: 'bottom', horizontal: 'right' }, PaperProps: { sx: { minWidth: 220, borderRadius: 2 } }, children: ROLE_PREVIEW_OPTIONS.map((option) => (_jsx(MenuItem, { onClick: () => {
                                                    dispatch(startRolePreview(option));
                                                    setPreviewMenuAnchor(null);
                                                    setDrawerOpen(false);
                                                    navigate('/');
                                                }, children: t(option.label) }, option.key))) }), drawerSections
                                            .flatMap((section) => section.items)
                                            .filter((item) => item.id === 'logout')
                                            .map((item) => (_jsx(ListItem, { disablePadding: true, children: _jsxs(ListItemButton, { onClick: () => {
                                                    item.onClick?.();
                                                    setDrawerOpen(false);
                                                }, sx: { px: 0 }, children: [item.icon && _jsx(ListItemIcon, { sx: { minWidth: 32 }, children: item.icon }), _jsx(ListItemText, { primary: item.label, primaryTypographyProps: {
                                                            fontWeight: 400,
                                                            color: '#FF5630'
                                                        } })] }) }, item.id)))] }), drawerSections.map((section, idx) => {
                                    const otherItems = section.items.filter((i) => i.id !== 'logout');
                                    if (otherItems.length === 0)
                                        return null;
                                    return (_jsx(Box, { sx: {
                                            borderRadius: 2,
                                            bgcolor: section.highlight ? theme.palette.grey[50] : 'transparent'
                                        }, children: otherItems.map((item) => {
                                            const hasSubmenu = item.hasSubmenu && item.submenuItems && item.submenuItems.length > 0;
                                            const isExpanded = expandedDrawerItems.has(item.id);
                                            // Check if any submenu item is active
                                            const hasActiveSubmenu = item.submenuItems?.some((subItem) => {
                                                if (!subItem.href)
                                                    return false;
                                                return (location.pathname === subItem.href ||
                                                    matchPath({ path: subItem.href, end: false }, location.pathname));
                                            });
                                            const isActive = !!item.href &&
                                                (location.pathname === item.href ||
                                                    matchPath({ path: item.href, end: false }, location.pathname));
                                            const match = item.href ? !!matchPath({ path: item.href, end: false }, location.pathname) : false;
                                            return (_jsxs(React.Fragment, { children: [_jsx(ListItem, { disablePadding: true, children: _jsxs(ListItemButton, { onClick: () => {
                                                                if (hasSubmenu) {
                                                                    handleDrawerSubmenuToggle(item.id);
                                                                }
                                                                else {
                                                                    if (item.href)
                                                                        navigate(item.href);
                                                                    item.onClick?.();
                                                                    setDrawerOpen(false);
                                                                }
                                                            }, selected: match || hasActiveSubmenu, sx: {
                                                                ...((isActive || hasActiveSubmenu) && {
                                                                    borderRadius: 1,
                                                                    '& .MuiListItemText-primary': {
                                                                        fontWeight: 600,
                                                                        color: theme.palette.primary.main
                                                                    },
                                                                    '& .MuiListItemIcon-root svg, & img': {
                                                                        color: theme.palette.primary.main
                                                                    }
                                                                })
                                                            }, children: [item.icon && (_jsx(ListItemIcon, { sx: { minWidth: 32 }, children: item.icon })), _jsx(ListItemText, { primary: item.label }), hasSubmenu && (_jsx(ChevronRightIcon, { sx: {
                                                                        color: theme.palette.grey[500],
                                                                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                                                        transition: theme.transitions.create('transform', {
                                                                            duration: theme.transitions.duration.short
                                                                        })
                                                                    } }))] }) }), hasSubmenu && (_jsx(Collapse, { in: isExpanded, timeout: 'auto', unmountOnExit: true, children: _jsx(Box, { sx: { pl: 4, pb: 0.5 }, children: item.submenuItems?.map((subItem) => {
                                                                const isSubActive = !!subItem.href &&
                                                                    (location.pathname === subItem.href ||
                                                                        matchPath({ path: subItem.href, end: false }, location.pathname));
                                                                const subMatch = subItem.href ? !!matchPath({ path: subItem.href, end: false }, location.pathname) : false;
                                                                return (_jsx(ListItem, { disablePadding: true, children: _jsxs(ListItemButton, { onClick: () => {
                                                                            if (subItem.href)
                                                                                navigate(subItem.href);
                                                                            subItem.onClick?.();
                                                                            setDrawerOpen(false);
                                                                        }, selected: subMatch, sx: {
                                                                            ...(isSubActive && {
                                                                                borderRadius: 1,
                                                                                '& .MuiListItemText-primary': {
                                                                                    fontWeight: 600,
                                                                                    color: theme.palette.primary.main
                                                                                },
                                                                                '& svg, & img': {
                                                                                    color: theme.palette.primary.main
                                                                                }
                                                                            })
                                                                        }, children: [_jsx(ListItemIcon, { sx: { minWidth: 32 }, children: _jsx(SubMenuIcon, { dotColor: isSubActive
                                                                                        ? theme.palette.primary.main
                                                                                        : theme.palette.grey[400] }) }), _jsx(ListItemText, { primary: subItem.label, primaryTypographyProps: {
                                                                                    fontSize: '14px'
                                                                                } })] }) }, subItem.id));
                                                            }) }) }))] }, item.id));
                                        }) }, idx));
                                })] }) }) }), _jsx(LanguageMenu, { anchorEl: languageAnchor, open: Boolean(languageAnchor), onClose: handleLanguageClose, selectedLang: selectedLang, onChangeLang: (lang) => setSelectedLang(lang), languages: languages }), _jsx(CompanyMenu, { anchorEl: companyAnchor, open: Boolean(companyAnchor), onClose: handleCompanyClose, onSelectCompany: handleSelectCompany }), _jsx(HelpCenter, {})] }) }));
};
export default AppLayoutContent;
